import { cloneElement, useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactElement, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cx } from "./cx";

export interface TooltipProps {
  content: ReactNode;
  children: ReactElement;
  side?: "top" | "bottom";
  /** 从触发元素居中或左缘展开；靠近屏幕边缘时自动让位。 */
  align?: "center" | "start";
  /** 允许换行（最宽 320px，并限制在视口内），用于字段说明。 */
  multiline?: boolean;
  delay?: number;
}

/**
 * 悬停或聚焦时显示说明，不承载操作。放在 body 上避免被抽屉滚动容器裁剪。
 * 浮层脱离了触发元素的 DOM 子树，所以显示时给触发元素挂 aria-describedby 让读屏读到说明；
 * 按 WCAG 1.4.13：Esc 可关闭；鼠标可以移到浮层上（例如选中复制完整 ID），移出触发元素与浮层后等同样的延迟再隐藏，
 * 这段延迟也让鼠标能越过两者之间的空隙；焦点还在触发元素上时移出不隐藏。键盘聚焦立即显示，悬停才延迟，避免读屏错过说明。
 * 浮层可悬停，会挡住它下面的内容；浮层上的点击不再沿 React 树冒泡到外层，但仍不要把 Tooltip 放进整行可点击的行或弹出面板的选项里。
 */
export function Tooltip({ content, children, side = "top", align = "center", multiline, delay = 150 }: TooltipProps) {
  const id = useId();
  const [show, setShow] = useState(false);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  const anchor = useRef<HTMLSpanElement>(null);
  const tip = useRef<HTMLSpanElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const hovered = useRef(false);
  const on = () => { hovered.current = true; window.clearTimeout(timer.current); if (!show) timer.current = window.setTimeout(() => setShow(true), delay); };
  // 浮层被 Esc 卸载时收不到 mouseleave，这里一并清掉悬停标记，否则之后键盘失焦也关不掉浮层。
  const off = useCallback(() => { hovered.current = false; window.clearTimeout(timer.current); setShow(false); setPosition(null); }, []);
  const leave = () => {
    hovered.current = false;
    window.clearTimeout(timer.current);
    if (anchor.current?.contains(document.activeElement)) return; // 焦点还在触发元素上，由失焦或 Esc 关闭
    timer.current = window.setTimeout(off, delay);
  };
  // 鼠标还在触发元素或浮层上时失焦不隐藏：点过触发元素再到浮层上选中文字，按下鼠标会让触发元素失焦。
  const blur = () => { if (!hovered.current) off(); };
  const focus = () => { window.clearTimeout(timer.current); setShow(true); };
  useEffect(() => () => window.clearTimeout(timer.current), []);
  // 焦点在触发元素里时由 onKeyDown 关闭并阻止冒泡，Esc 不会连带关掉外层的抽屉或对话框（它们在 document 上监听）；
  // 只悬停时焦点在别处，这里只关浮层、不拦截，Esc 照常交给焦点所在的界面。
  useEffect(() => {
    if (!show) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !anchor.current?.contains(document.activeElement)) off(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [show, off]);
  const onKeyDown = (e: ReactKeyboardEvent<HTMLSpanElement>) => {
    if (!show || e.key !== "Escape") return;
    e.stopPropagation(); // React 的监听先于 document 上的原生监听
    off();
  };
  useLayoutEffect(() => {
    if (!show) return;
    const place = () => {
      const a = anchor.current!.getBoundingClientRect();
      const t = tip.current!.getBoundingClientRect();
      const left = align === "start" ? a.left : a.left + (a.width - t.width) / 2;
      let top = side === "bottom" ? a.bottom + 4 : a.top - t.height - 4;
      if (top < 8) top = a.bottom + 4;
      if (top + t.height > window.innerHeight - 8) top = a.top - t.height - 4;
      setPosition({
        left: Math.max(8, Math.min(left, window.innerWidth - t.width - 8)),
        top: Math.max(8, Math.min(top, window.innerHeight - t.height - 8)),
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
  }, [show, side, align, content]);
  // 浮层经 portal 渲染，React 的鼠标移入移出按组件树而不是 DOM 树计算：从空隙移到浮层会触发锚点的 onMouseEnter，
  // 移出浮层会触发锚点的 onMouseLeave，在锚点与浮层之间直接移动则都不触发，所以只在锚点上监听即可。
  return (
    <span ref={anchor} className="ax-tooltip-anchor" onMouseEnter={on} onMouseLeave={leave} onFocusCapture={focus} onBlurCapture={blur} onKeyDown={onKeyDown}>
      {show ? cloneElement(children, { "aria-describedby": [children.props["aria-describedby"], id].filter(Boolean).join(" ") }) : children}
      {show && createPortal(
        <span ref={tip} id={id} className={cx("ax-tooltip", side === "bottom" && "bottom", align === "start" && "start", multiline && "multiline")} role="tooltip" onClick={(e) => e.stopPropagation()}
          style={{ position: "fixed", bottom: "auto", transform: "none", left: position?.left ?? 0, top: position?.top ?? 0, visibility: position ? "visible" : "hidden", maxWidth: multiline ? "min(320px, calc(100vw - 16px))" : "calc(100vw - 16px)" }}>{content}</span>,
        document.body,
      )}
    </span>
  );
}
